import { type Action, combineReducers } from 'redux';

import type { LanguageEnum } from 'js/src/client';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import {
    endLexicalControllingItsOwnState,
    startLexicalControllingItsOwnState,
} from 'js/src/data/transcript/transcript-actions';
import {
    clearActiveTranslationSceneGroupId,
    removeTranslatedEditor,
    storeActiveTranslationSceneGroupId,
    storeTranslatedEditor,
    storeTranslatedEditors,
} from 'js/src/data/translated-editors/translated-editors-actions';
import type {
    TranscriptTranslationsType,
    TranslatedEditorsType,
} from 'js/src/data/translated-editors/translated-editors-types';

type TranslatedEditorsTable = PlainJsStoreOrmData<TranslatedEditorsType, string>;

const setLexicalControlsItsOwnState = (
    state: TranslatedEditorsTable,
    sceneGroupId: string,
    langCode: string,
    lexicalControlsItsOwnState: boolean,
) => {
    const orm = new PlainJsStoreORM(state);
    if (!orm.contains(sceneGroupId)) {
        return state;
    }
    orm.updateById(sceneGroupId, (item) => {
        const translations: Readonly<
            Record<string, TranscriptTranslationsType[LanguageEnum] | undefined>
        > = item.translations;
        return {
            ...item,
            translations: {
                ...item.translations,
                [langCode]: { ...translations[langCode], lexicalControlsItsOwnState },
            },
        };
    });
    return orm.data;
};

export function translated_editors(
    state: TranslatedEditorsTable = PlainJsStoreORM.createEmpty<TranslatedEditorsType, string>()
        .data,
    action: Action,
) {
    if (storeTranslatedEditors.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.pushItem(action.payload, 'sceneGroupId');
        return orm.data;
    } else if (storeTranslatedEditor.match(action)) {
        const orm = new PlainJsStoreORM(state);
        const { sceneGroupId, editor, langCode, lexicalControlsItsOwnState } = action.payload;
        const translations = {
            [langCode]: {
                editor,
                lexicalControlsItsOwnState,
            },
        } as TranscriptTranslationsType;
        orm.pushItem({ sceneGroupId, translations }, 'sceneGroupId');
        return orm.data;
    } else if (removeTranslatedEditor.match(action)) {
        const orm = new PlainJsStoreORM(state);
        orm.delete([action.payload.sceneGroupId]);
        return orm.data;
    } else if (startLexicalControllingItsOwnState.match(action)) {
        const langCode = action.payload.langCode;
        if (!langCode) {
            // this redux action is meant for the "main" transcript, not the translated transcript
            // so we don't need to do anything here
            return state;
        }
        return setLexicalControlsItsOwnState(state, action.payload.sceneGroupId, langCode, true);
    } else if (endLexicalControllingItsOwnState.match(action)) {
        const langCode = action.payload.langCode;
        if (!langCode) {
            // this redux action is meant for the "main" transcript, not the translated transcript
            // so we don't need to do anything here
            return state;
        }
        return setLexicalControlsItsOwnState(state, action.payload.sceneGroupId, langCode, false);
    }

    return state;
}

export function active_translation_scene_group_id(state = '', action: Action) {
    if (storeActiveTranslationSceneGroupId.match(action)) {
        return action.payload.sceneGroupId;
    } else if (clearActiveTranslationSceneGroupId.match(action)) {
        return '';
    }

    return state;
}

export const transcript_translations = combineReducers({
    translated_editors,
    active_translation_scene_group_id,
});

export type TranslatedEditorsState = ReturnType<typeof transcript_translations>;
